/* import __COLOCATED_TEMPLATE__ from './svg-waveform-graph.hbs'; */
import Component from '@glimmer/component';
import Owner from '@ember/owner';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { format } from 'd3-format';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import type IntlService from 'ember-intl/services/intl';

import Zoom, { ZoomRect, ZoomPosition, ZoomLimits } from '../models/graphs/zoom';
import type { WaveformGraphLimits } from '../models/graphs/waveform-graph';

const leftLabelOffset = 55,
  bottomLabelOffset = 60;

interface SvgWaveformGraphArgs {
  dataPoints: number[][][];
  limitPoints: WaveformGraphLimits;
  suppress: boolean;
}
interface SvgWaveformGraphSignature {
  Args: SvgWaveformGraphArgs;
  Element: SVGElement;
}
/**
 * A field for display of frequency data
 *
 * @extends {Component<SvgWaveformGraphSignature>}
 */
export default class SvgWaveformGraph extends Component<SvgWaveformGraphSignature> {
  @service declare intl: IntlService;
  zoom = new Zoom();
  leftMargin = 70;
  topMargin = 10;
  rightMargin = 5;
  bottomMargin = 65;
  xTitle: string | null = null;
  yTitle: string | null = null;
  svgElement: SVGElement | null = null;

  @tracked limits: WaveformGraphLimits | null = null;
  @tracked width = 620;
  @tracked height = 290;
  @tracked showReturnTo100 = false;
  @tracked isSelectionActive?: ZoomPosition;
  @tracked zoomRectangle?: ZoomRect;

  constructor(owner: Owner, args: SvgWaveformGraphArgs) {
    super(owner, args);
    this.xTitle = this.intl.t('waveform-graph.time_axis_text');
    this.yTitle = this.intl.t('waveform-graph.amplitude_axis_text');
    // Provisional dimensions for initial layout, just to ensure everything is there
    // Real dimensions come from CSS after we have a tag to query
    this.returnTo100();
  }
  @action argsUpdated() {
    this.returnTo100();
  }
  @action onMouseup(/*event: Event*/) {
    if (this.isSelectionActive) {
      this.handleMouseUp();
    }
  }
  @action inserted(element: SVGElement) {
    this.svgElement = element;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.addEventListener('mouseup', this.onMouseup);
    const rect = element.getBoundingClientRect();
    if (rect.width !== 620) {
      this.width = element.clientWidth;
    }
    if (rect.height !== 290) {
      this.height = element.clientHeight;
    }
  }
  @action handleMouseDown(event: MouseEvent) {
    if (event.which === 1 || event.which === 2 || event.which === 3) {
      if (!this.args.suppress && this.svgElement) {
        const elem = this.svgElement.querySelector('.frame');
        if (elem && this.limits) {
          const zoomLimits: ZoomLimits = {
            minX: this.limits.mintime,
            maxX: this.limits.maxtime,
            minY: this.limits.minamp,
            maxY: this.limits.maxamp,
          };
          this.zoomRectangle = undefined;
          const xScale = scaleLinear().domain([this.limits.mintime, this.limits.maxtime]).range([0, this.chartwidth]);
          const xFormatter = format('.4s');
          const xTicks = xScale.ticks(this.limits.tickstime).map((tick: number) => ({
            value: xFormatter(tick),
            x: xScale(tick),
            y: this.chartheight,
          }));
          const yScale = scaleLinear().domain([this.limits.minamp, this.limits.maxamp]).range([this.chartheight, 0]);
          const yFormatter = format('.1f');
          const yTicks = yScale.ticks(this.limits.tickstime).map((tick: number) => ({
            value: yFormatter(tick),
            x: 0,
            y: yScale(tick),
          }));
          this.isSelectionActive = this.zoom.startZoomSelect(event, elem, xTicks, yTicks, zoomLimits);
        }
      }
    }
  }
  @action handleMouseMove(event: MouseEvent) {
    if (!this.args.suppress && this.isSelectionActive) {
      this.zoomRectangle = this.zoom.moveZoomSelect(event);
    }
  }
  @action handleMouseUp() {
    if (!this.args.suppress) {
      const zoomLimits = this.zoom.stopZoomSelect(15, 15);
      if (zoomLimits) {
        this.limits = {
          mintime: zoomLimits.minX,
          maxtime: zoomLimits.maxX,
          tickstime: this.limits ? this.limits.tickstime : 1,
          minamp: zoomLimits.minY,
          maxamp: zoomLimits.maxY,
          ticksamp: this.limits ? this.limits.ticksamp : 5,
        };
        this.showReturnTo100 = true;
      }
    }
    this.zoomRectangle = undefined;
    this.isSelectionActive = undefined;
  }
  returnTo100() {
    this.zoomRectangle = undefined;
    this.limits = this.args.limitPoints;
    this.showReturnTo100 = false;
  }

  get data() {
    if (this.args.suppress) {
      return [];
    }
    return this.args.dataPoints;
  }
  get chartwidth() {
    return this.width - (this.leftMargin + this.rightMargin);
  }
  get chartheight() {
    return this.height - (this.topMargin + this.bottomMargin);
  }
  get xTitleCenter() {
    return { x: this.chartwidth / 2, y: this.chartheight + bottomLabelOffset };
  }
  get yTitleCenter() {
    return { x: -leftLabelOffset, y: this.chartheight / 2 };
  }
  get xTicks() {
    if (this.limits) {
      const xScale = scaleLinear().domain([this.limits.mintime, this.limits.maxtime]).range([0, this.chartwidth]);
      const xFormatter = format('.4s');
      return xScale.ticks(this.limits.tickstime).map((tick: number) => ({
        value: xFormatter(tick),
        x: xScale(tick),
        y: this.chartheight,
      }));
    }
  }
  get yTicks() {
    if (this.limits) {
      const yScale = scaleLinear().domain([this.limits.minamp, this.limits.maxamp]).range([this.chartheight, 0]);
      const yFormatter = format('.1f');
      return yScale
        .ticks(this.limits.ticksamp)
        .map((tick: number) => ({ value: yFormatter(tick), x: 0, y: yScale(tick) }));
    }
  }
  get path() {
    let result = null;
    const points = this.data[0] as [number, number][];
    if (!this.args.suppress && this.limits && points) {
      const xScale = scaleLinear().domain([this.limits.mintime, this.limits.maxtime]).range([0, this.chartwidth]);
      const yScale = scaleLinear().domain([this.limits.minamp, this.limits.maxamp]).range([this.chartheight, 0]);
      result = line()
        .x((d: [number, number]) => xScale(d[0]))
        .y((d: [number, number]) => yScale(d[1]))(points);
    }
    return result;
  }
  @action resetZoom() {
    this.returnTo100();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SvgWaveformGraph: typeof SvgWaveformGraph;
  }
}
